// BulkAction.jsx
import React from "react";
import PropTypes from "prop-types";
import { SortAsc } from "lucide-react";

function useCompatIdFallback() {
  const [id] = React.useState(
    () => `ba-${Math.random().toString(36).slice(2)}`,
  );

  return id;
}

const useCompatId =
  typeof React.useId === "function" ? React.useId : useCompatIdFallback;

function BulkAction({
  options = [],
  selectedIds = [],
  onAction,
  label = "Bulk action",
  placeholder = "Select…",
  className = "",
  icon = <SortAsc size={16} />,
  defaultAction = "",
  disabled = false,
  requireSelection = true,
  id,
}) {
  const [action, setAction] = React.useState(defaultAction);
  const [busy, setBusy] = React.useState(false);

  const autoId = useCompatId();
  const selectId = id ?? autoId;

  const hasSelection = Array.isArray(selectedIds) && selectedIds.length > 0;

  const selectionRequiredButMissing = requireSelection && !hasSelection;

  /*
   * The complete dropdown is disabled only for an explicit disabled state,
   * while an action is running, or when there are no available options.
   *
   * An empty checkbox selection does not disable the dropdown.
   */
  const isSelectDisabled = disabled || busy || options.length === 0;

  const isOptionDisabled = React.useCallback(
    (option) => Boolean(option?.disabled) || selectionRequiredButMissing,
    [selectionRequiredButMissing],
  );

  /*
   * Reset the current action if its option becomes unavailable, such as when
   * the parent clears all selected checkboxes after completing an action.
   */
  React.useEffect(() => {
    if (!action) {
      return;
    }

    const currentOption = options.find((option) => option.value === action);

    if (!currentOption || isOptionDisabled(currentOption)) {
      setAction("");
    }
  }, [action, options, isOptionDisabled]);

  const handleChange = async (event) => {
    const value = event.target.value;

    if (!value) {
      setAction("");
      return;
    }

    const selectedOption = options.find((option) => option.value === value);

    /*
     * Native disabled options normally cannot trigger onChange, but these
     * checks also protect programmatic or unexpected change events.
     */
    if (
      !selectedOption ||
      isSelectDisabled ||
      isOptionDisabled(selectedOption)
    ) {
      setAction("");
      return;
    }

    setAction(value);

    if (!onAction) {
      return;
    }

    try {
      setBusy(true);
      await onAction(value, selectedIds);
    } finally {
      setAction(defaultAction);
      setBusy(false);
    }
  };

  return (
    <div className={`bulk-action-wrap ${className}`.trim()} aria-busy={busy}>
      <label className="bulk-action-label" htmlFor={selectId}>
        {icon}
        <span>{label}</span>
      </label>

      <select
        id={selectId}
        value={action}
        onChange={handleChange}
        className="bulk-action-select"
        disabled={isSelectDisabled}
        aria-disabled={isSelectDisabled}
      >
        <option value="" disabled>
          {options.length ? placeholder : "No actions"}
        </option>

        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={isOptionDisabled(option)}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

BulkAction.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,

  selectedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,

  onAction: PropTypes.func.isRequired,
  label: PropTypes.node,
  placeholder: PropTypes.node,
  className: PropTypes.string,
  icon: PropTypes.node,
  defaultAction: PropTypes.string,
  disabled: PropTypes.bool,
  requireSelection: PropTypes.bool,
  id: PropTypes.string,
};

export default React.memo(BulkAction);
